import { getZonedDate } from '@leagueplatform/web-common';
import { MasonryChartInterval } from '#types/masonry-chart.type.ts';
import { getMonthlyXAxisTicks } from './use-monthly-ticks';

function uniqueByKey<T>(items: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  items.forEach((item) => {
    const key = keyFn(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  });
  return result;
}

function middleByKey<T>(items: T[], keyFn: (item: T) => string): T[] {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyFn(item);
    const arr = groups.get(key) ?? [];
    arr.push(item);
    groups.set(key, arr);
  });
  return Array.from(groups.values()).map(
    (group) => group[Math.floor(group.length / 2)],
  );
}

/**
 * Picks one timestamp per "display unit" of the given interval so the
 * x-axis renders exactly one label per visible bucket — regardless of
 * how many datums the BE produces (e.g. monthly aggregation on a
 * 5-yearly chart still yields 5 yearly ticks).
 */
export function getIntervalXAxisTicks(
  timestamps: string[],
  interval: MasonryChartInterval,
  defaultTimezone?: string,
): string[] | undefined {
  if (timestamps.length === 0) return [];

  switch (interval) {
    case 'weekly':
      // One tick per day; range charts may have multiple metric series per day.
      return uniqueByKey(timestamps, (ts) => {
        const date = getZonedDate(ts, defaultTimezone);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      });
    case 'monthly':
      return getMonthlyXAxisTicks(timestamps, defaultTimezone);
    case 'yearly':
      // First datum of each (year, month).
      return uniqueByKey(timestamps, (ts) => {
        const date = getZonedDate(ts, defaultTimezone);
        return `${date.getFullYear()}-${date.getMonth()}`;
      });
    case '5-yearly':
      // First datum of each year.
      return uniqueByKey(timestamps, (ts) =>
        String(getZonedDate(ts, defaultTimezone).getFullYear()),
      );
    default:
      return undefined;
  }
}

/**
 * Like `getIntervalXAxisTicks` but returns the **middle** timestamp of
 * each bucket — used for axis labels so they sit visually in the center
 * of their bucket (e.g. July for a year bucket of 12 monthly datums).
 *
 * For intervals where each tick already corresponds to a single datum
 * (weekly, monthly, yearly with monthly aggregation), this returns the
 * same values as `getIntervalXAxisTicks`.
 */
export function getIntervalXAxisLabelTicks(
  timestamps: string[],
  interval: MasonryChartInterval,
  defaultTimezone?: string,
): string[] | undefined {
  if (timestamps.length === 0) return [];

  switch (interval) {
    case 'weekly':
      // One label per day; mirrors getIntervalXAxisTicks weekly dedup.
      return uniqueByKey(timestamps, (ts) => {
        const date = getZonedDate(ts, defaultTimezone);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      });
    case 'monthly':
      return getMonthlyXAxisTicks(timestamps, defaultTimezone);
    case 'yearly':
      // Middle datum of each (year, month) bucket.
      return middleByKey(timestamps, (ts) => {
        const date = getZonedDate(ts, defaultTimezone);
        return `${date.getFullYear()}-${date.getMonth()}`;
      });
    case '5-yearly':
      // Middle datum of each year bucket.
      return middleByKey(timestamps, (ts) =>
        String(getZonedDate(ts, defaultTimezone).getFullYear()),
      );
    default:
      return undefined;
  }
}
